import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { showErrorToast, showSuccessToast } from '../../util/toast';
import { ListingType, TRANSACTION_PROCESS_ALIAS, UNIT_TYPE } from '../../util/enums';
import { CAMPAIGN, EDIT } from '../../util/constants';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import moment from 'moment';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const CREATE_CAMPAIGN_LISTING_REQUEST =
  'app/CampaignPostingPage/CREATE_CAMPAIGN_LISTING_REQUEST';
export const CREATE_CAMPAIGN_LISTING_SUCCESS =
  'app/CampaignPostingPage/CREATE_CAMPAIGN_LISTING_SUCCESS';
export const CREATE_CAMPAIGN_LISTING_ERROR =
  'app/CampaignPostingPage/CREATE_CAMPAIGN_LISTING_ERROR';

export const SHOW_LISTING_REQUEST = 'app/CampaignPostingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/CampaignPostingPage/SHOW_LISTING_ERROR';

// ================ Reducer ================ //

const initialState = {
  createCampaignListingInProgress: false,
  createCampaignListingError: null,
  id: null,
  showListingError: null,
};

const CampaignPostingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case CREATE_CAMPAIGN_LISTING_REQUEST:
      return { ...state, createCampaignListingInProgress: true, createCampaignListingError: null };
    case CREATE_CAMPAIGN_LISTING_SUCCESS:
      return { ...state, createCampaignListingInProgress: false, createCampaignListingError: null };
    case CREATE_CAMPAIGN_LISTING_ERROR:
      return {
        ...state,
        createCampaignListingInProgress: false,
        createCampaignListingError: payload,
      };
    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };
    default:
      return state;
  }
};

export default CampaignPostingPageReducer;

// ================ Selectors ================ //

export const createCampaignListingSelector = state => {
  const { createCampaignListingInProgress, createCampaignListingError } = state.PerksPostPage;
  return {
    createCampaignListingInProgress,
    createCampaignListingError,
  };
};

// ================ Action creators ================ //

export const createCampaignListingRequest = () => ({ type: CREATE_CAMPAIGN_LISTING_REQUEST });
export const createCampaignListingSuccess = () => ({
  type: CREATE_CAMPAIGN_LISTING_SUCCESS,
});
export const createCampaignListingError = error => ({
  type: CREATE_CAMPAIGN_LISTING_ERROR,
  error: true,
  payload: error,
});
export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const createCampaignListing = params => async (dispatch, getState, sdk) => {
  dispatch(createCampaignListingRequest());
  try {
    // Explicitly define allowed fields, avoiding spread of rest
    const {
      title,
      description,
      price,
      userType,
      id,
      preferredCompletionDate,
      paidAdsBudget,
      parentListingTitle,
      parentListingId,
      ...rest
    } = params || {};
    const updatedValues = {
      title,
      description,
      ...(price?.amount && { price }), // Include price only if price.amount exists
      publicData: {
        transactionProcessAlias: TRANSACTION_PROCESS_ALIAS.DEFAULT_PURCHASE,
        unitType: UNIT_TYPE.ITEM,
        listingType: ListingType.PURCHASE,
        listingCategory: `${userType}${CAMPAIGN}`,
        ...(parentListingId && { parentListingId }),
        ...(parentListingTitle && { parentListingTitle }),
        ...(preferredCompletionDate
          ? { preferredCompletionDate: moment(preferredCompletionDate).unix() }
          : {}),
        ...(paidAdsBudget ? { paidAdsBudget: paidAdsBudget?.amount } : {}),
        ...rest,
      },
    };

    // Include id for updates
    if (id) {
      updatedValues.id = new UUID(id);
    }

    const response = id
      ? await sdk.ownListings.update(updatedValues, { expand: true })
      : await sdk.ownListings.create(updatedValues, { expand: true });
    const listing = response?.data?.data;
    const listingId = listing?.id?.uuid;

    if (listingId) {
      await sdk.stockAdjustments.create(
        {
          listingId,
          quantity: 1,
        },
        {
          expand: true,
          include: ['ownListing.currentStock'],
        }
      );
    }

    dispatch(createCampaignListingSuccess());
    showSuccessToast('Campaign created successfully');
    return listing;
  } catch (error) {
    dispatch(createCampaignListingError(storableError(error)));
    showErrorToast('Campaign creation failed');
  }
};

export const showListing = (listingId, config) => async (dispatch, getState, sdk) => {
  // Destructure layout config with defaults
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // Dispatch initial actions
  dispatch(showListingRequest(listingId));
  dispatch(fetchCurrentUser());

  // Build the query parameters
  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'author.publicData', 'images', 'currentStock'],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,
      // Social media
      'variants.facebook',
      'variants.twitter',
      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  try {
    const data = await sdk.ownListings.show(params);
    const listingFields = config?.listing?.listingFields;
    const sanitizeConfig = { listingFields };
    dispatch(addMarketplaceEntities(data, sanitizeConfig));
    return data;
  } catch (error) {
    dispatch(showListingError(storableError(error)));
  }
};

export const loadData = (params, search, config) => dispatch => {
  const listingId = new UUID(params.id);
  const type = params?.type;
  if (type === EDIT) {
    dispatch(showListing(listingId, config));
  }
  return dispatch(fetchCurrentUser());
};
